import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  delay,
  filter,
  forkJoin,
  from,
  groupBy,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import {
  BankVerificationIdQueryV1,
  BankVerificationRequestV1,
  EmailDomainVerificationIdQueryV1,
  EuVatVerificationIdQueryV1,
  GstVerificationIdQueryV1,
  IbanVerificationIdQueryV1,
  TinVerificationIdQueryV1,
  VerificationFieldMappingV1,
  VerificationResultResponseV1,
} from 'src/app/api/models';
import { BankVerificationV1Service } from 'src/app/api/services/bank-verification-v-1.service';
import { TinVerificationV1Service } from 'src/app/api/services/tin-verification-v-1.service';
import { IbanVerificationV1Service } from 'src/app/api/services/iban-verification-v-1.service';
import { GstVerificationV1Service } from 'src/app/api/services/gst-verification-v-1.service';
import { EmailDomainVerificationV1Service } from 'src/app/api/services/email-domain-verification-v-1.service';
import { EuVatVerificationV1Service } from 'src/app/api/services/eu-vat-verification-v-1.service';
import { VerificationResultsV1Service } from 'src/app/api/services/verification-results-v-1.service';
import { TenantService } from 'src/app/authentication/service/tenantService';
import { VerificationSupportV1Service } from 'src/app/api/services/verification-support-v-1.service';
import { VerificationFieldConfig } from 'src/app/requests/request-verifications/types/types';
import {
  DetectedField,
  detectVerifiableFields,
  mergeRequestFormData,
} from 'src/app/requests/request-verifications/verification-cache/verification-field-detector';
import {
  buildBankBody,
  buildEmailBody,
  buildEuVatBody,
  buildGstBody,
  buildIbanBody,
  buildTinBody,
} from 'src/app/requests/request-verifications/verification-cache/verification-body-builders';
import {
  CacheVerificationType,
  VERIFICATION_MAX_POLLS,
  VERIFICATION_POLL_INTERVAL_MS,
} from 'src/app/requests/request-verifications/verification-cache/verification-cache.models';

import {
  autoTriggerVerifications,
  hydrateVerificationResults,
  pollFailed,
  pollResultReceived,
  pollTimeout,
  pollVerification,
  reverifyField,
  triggerVerification,
  verificationFailed,
  verificationResponseReceived,
} from '../../actions/verification-cache/verification-cache.actions';
import { selectRequestFormRequest, selectVerificationCacheEntry } from '../../selector';

function safeStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

const VERIFICATION_DEFAULT_VALIDITY_DAYS = 30;

function isExpired(runOn: string | null | undefined, validityDays: number | null): boolean {
  const days = validityDays ?? VERIFICATION_DEFAULT_VALIDITY_DAYS;
  if (days === 0) return true; // 0 = always re-verify, never use cache
  if (!runOn) return false;
  const runDate = new Date(runOn).getTime();
  if (Number.isNaN(runDate)) return false;
  return Date.now() > runDate + days * 24 * 60 * 60 * 1000;
}

function resolveBankIsBusiness(
  fieldData: Record<string, unknown>,
  countryCode: string,
  forcePersonal = false
): boolean | undefined {
  // account_type can be authored as a labelled boolean ("true"/"false"),
  // a real boolean, or the legacy string convention ("business"/"personal").
  const accountType = fieldData['account_type'];
  if (accountType != null) {
    if (typeof accountType === 'boolean') return accountType;
    const s = safeStr(accountType).toLowerCase();
    if (s === 'business' || s === 'true') return true;
    if (s === 'personal' || s === 'false') return false;
  }
  // SA also accepts/requires isBusiness, but unlike CN/US there is no safe default
  // — Signzy uses it together with creditorIdentifierType to pick the right
  // identifier flavour, so the form must collect account_type explicitly.
  if (!forcePersonal && (countryCode === 'CN' || countryCode === 'US')) {
    return true;
  }
  return undefined;
}

/**
 * Returns true when the bank verification should retry as personal if business fails.
 * Currently applies to USA only — China has its own field-based logic and is excluded.
 * Isolated here so the condition is easy to refine later.
 */
function shouldFallbackToPersonal(countryCode: string, isBusiness: boolean | undefined): boolean {
  return countryCode === 'US' && isBusiness === true;
}

function onPollResult(requestId: string, key: string, resultId: string) {
  return (response: VerificationResultResponseV1) => {
    if (response.status === 'PENDING') {
      return of(pollResultReceived({ requestId, key, response }), pollVerification({ requestId, key, resultId }));
    }
    return of(pollResultReceived({ requestId, key, response }));
  };
}

@Injectable()
export class VerificationCacheEffects {
  private fieldConfigPromise: Promise<VerificationFieldConfig[]> | null = null;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly tenantService: TenantService,
    private readonly bankApi: BankVerificationV1Service,
    private readonly tinApi: TinVerificationV1Service,
    private readonly ibanApi: IbanVerificationV1Service,
    private readonly gstApi: GstVerificationV1Service,
    private readonly euVatApi: EuVatVerificationV1Service,
    private readonly emailApi: EmailDomainVerificationV1Service,
    private readonly resultsApi: VerificationResultsV1Service,
    private readonly verificationSupportV1Service: VerificationSupportV1Service
  ) {}

  private getFieldConfig(): Promise<VerificationFieldConfig[]> {
    this.fieldConfigPromise ??= new Promise((resolve, reject) =>
      this.verificationSupportV1Service.getVerificationFieldMappingsV1().subscribe({
        next: (cfg) => {
          const config: VerificationFieldConfig[] | null =
            (cfg as VerificationFieldMappingV1[] | null)?.map(
              (c): VerificationFieldConfig => ({
                type: c.type ?? '',
                anchorField: c.anchorField ?? '',
                countries: c.countries ?? [],
                fields: c.fields ?? {},
              })
            ) ?? null;
          if (config?.length) {
            resolve(config);
          } else {
            this.fieldConfigPromise = null;
            reject(new Error('Verification field config unavailable'));
          }
        },
        error: (err) => {
          this.fieldConfigPromise = null;
          reject(err);
        },
      })
    );
    return this.fieldConfigPromise;
  }

  autoTrigger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(autoTriggerVerifications),
      withLatestFrom(
        this.tenantService.isVerificationCachingEnabled(),
        this.tenantService.getVerificationValidityDays(),
        this.store.select(selectRequestFormRequest)
      ),
      filter(([, enabled]) => enabled),
      mergeMap(([{ requestId, forceRefresh }, , validityDays, request]) => {
        if (!request) return of();

        const mergedData = mergeRequestFormData(
          request as {
            internalData?: Record<string, unknown> | null;
            externalData?: Record<string, unknown> | null;
            notesData?: Record<string, unknown> | null;
          }
        );

        return from(this.getFieldConfig()).pipe(
          // Without field mappings nothing can be detected; end quietly instead of erroring the effect.
          catchError(() => of()),
          switchMap((fieldConfig) => {
            const fields = detectVerifiableFields(mergedData, fieldConfig);
            if (fields.length === 0) return of();
            if (forceRefresh) {
              return from(fields.map((field) => triggerVerification({ requestId, field, forceRefresh: true })));
            }
            return this.resolveAndSchedule(requestId, fields, fieldConfig, validityDays);
          })
        );
      })
    )
  );

  reverify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reverifyField),
      map(({ requestId, field }) => triggerVerification({ requestId, field, forceRefresh: true }))
    )
  );

  triggerVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerVerification),
      groupBy(({ field }) => field.key),
      mergeMap((group$) =>
        group$.pipe(
          switchMap(({ requestId, field, forceRefresh }) =>
            from(this.getFieldConfig()).pipe(
              switchMap((fieldConfig) => this.runVerification(requestId, field, forceRefresh, fieldConfig)),
              // A failed mappings fetch must settle the entry, otherwise it stays 'loading' forever.
              catchError((error) => of(verificationFailed({ requestId, key: field.key, error })))
            )
          )
        )
      )
    )
  );

  startPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verificationResponseReceived),
      filter(({ response }) => response.status === 'PENDING'),
      map(({ requestId, key, response }) => pollVerification({ requestId, key, resultId: response.resultId! }))
    )
  );

  poll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pollVerification),
      groupBy(({ key }) => key),
      mergeMap((group$) =>
        group$.pipe(switchMap(({ requestId, key, resultId }) => this.executePoll(requestId, key, resultId)))
      )
    )
  );

  private resolveAndSchedule(
    requestId: string,
    fields: DetectedField[],
    fieldConfig: VerificationFieldConfig[],
    validityDays: number | null
  ) {
    const resolveBody = fields.map(
      (
        field
      ):
        | BankVerificationIdQueryV1
        | TinVerificationIdQueryV1
        | IbanVerificationIdQueryV1
        | GstVerificationIdQueryV1
        | EuVatVerificationIdQueryV1
        | EmailDomainVerificationIdQueryV1 => {
        if (field.type === 'bank') {
          return {
            type: 'BANK',
            countryCode: field.countryCode,
            request: buildBankBody(requestId, field, fieldConfig, undefined, false),
          };
        }
        if (field.type === 'tin') {
          return {
            type: 'TIN',
            countryCode: field.countryCode,
            request: buildTinBody(requestId, field, fieldConfig, false),
          };
        }
        if (field.type === 'gst') {
          return {
            type: 'GST',
            countryCode: field.countryCode,
            request: buildGstBody(requestId, field, fieldConfig, false),
          };
        }
        if (field.type === 'eu_vat') {
          return {
            type: 'EU_VAT',
            countryCode: field.countryCode,
            request: buildEuVatBody(requestId, field, fieldConfig, false),
          };
        }
        if (field.type === 'email_domain') {
          return { type: 'EMAIL_DOMAIN', request: buildEmailBody(requestId, field, false) };
        }
        return { type: 'IBAN', request: buildIbanBody(requestId, field, fieldConfig, false) };
      }
    );

    return forkJoin({
      cachedResults: this.resultsApi
        .getVerificationResultsV1({ requestId })
        .pipe(catchError(() => of([] as VerificationResultResponseV1[]))),
      resolvedIds: this.resultsApi.resolveVerificationIdsV1({ body: resolveBody }).pipe(catchError(() => of(null))),
    }).pipe(
      mergeMap(({ cachedResults, resolvedIds }) =>
        this.classifyResults(requestId, fields, cachedResults, resolvedIds, validityDays)
      )
    );
  }

  private classifyResults(
    requestId: string,
    fields: DetectedField[],
    cachedResults: VerificationResultResponseV1[],
    resolvedIds: Array<{ resultId?: string }> | null,
    validityDays: number | null
  ) {
    const toHydrate: Array<{
      key: string;
      type: CacheVerificationType;
      panelIndex: number;
      response: VerificationResultResponseV1;
      isExpired: boolean;
    }> = [];
    const toTrigger: DetectedField[] = [];
    const toRetrigger: DetectedField[] = [];

    const byResultId = new Map(cachedResults.map((r) => [r.resultId, r]));

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const { key, type, panelIndex } = field;
      const resultId = resolvedIds?.[i]?.resultId;
      const cached = resultId ? byResultId.get(resultId) : undefined;

      if (cached && cached.status !== 'PENDING') {
        if (isExpired(cached.runOn, validityDays)) {
          toRetrigger.push(field);
        } else {
          toHydrate.push({ key, type, panelIndex, response: cached, isExpired: false });
        }
      } else {
        toTrigger.push(field);
      }
    }

    return from([
      ...(toHydrate.length > 0 ? [hydrateVerificationResults({ requestId, results: toHydrate })] : []),
      ...toTrigger.map((field) => triggerVerification({ requestId, field, forceRefresh: false })),
      ...toRetrigger.map((field) => triggerVerification({ requestId, field, forceRefresh: true })),
    ]);
  }

  private runVerification(
    requestId: string,
    field: DetectedField,
    forceRefresh: boolean,
    fieldConfig: VerificationFieldConfig[]
  ) {
    const { key, type, panelIndex, countryCode, fieldData } = field;

    if (type === 'tin') {
      return this.tinApi
        .verifyTinv1({ countryCode: 'US', body: buildTinBody(requestId, field, fieldConfig, forceRefresh) })
        .pipe(
          map((response) =>
            verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })
          ),
          catchError((error) => of(verificationFailed({ requestId, key, error })))
        );
    }

    if (type === 'iban') {
      return this.ibanApi.verifyIbanv1({ body: buildIbanBody(requestId, field, fieldConfig, forceRefresh) }).pipe(
        map((response) =>
          verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })
        ),
        catchError((error) => of(verificationFailed({ requestId, key, error })))
      );
    }

    if (type === 'gst') {
      return this.gstApi
        .verifyGstv1({ countryCode, body: buildGstBody(requestId, field, fieldConfig, forceRefresh) })
        .pipe(
          map((response) =>
            verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })
          ),
          catchError((error) => of(verificationFailed({ requestId, key, error })))
        );
    }

    if (type === 'eu_vat') {
      return this.euVatApi
        .verifyEuVatV1({ countryCode, body: buildEuVatBody(requestId, field, fieldConfig, forceRefresh) })
        .pipe(
          map((response) =>
            verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })
          ),
          catchError((error) => of(verificationFailed({ requestId, key, error })))
        );
    }

    if (type === 'email_domain') {
      return this.emailApi.verifyEmailDomainV1({ body: buildEmailBody(requestId, field, forceRefresh) }).pipe(
        map((response) =>
          verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })
        ),
        catchError((error) => of(verificationFailed({ requestId, key, error })))
      );
    }

    const isBusiness = resolveBankIsBusiness(fieldData, countryCode);
    const bankBody = buildBankBody(requestId, field, fieldConfig, isBusiness, forceRefresh);

    return this.bankApi.verifyBankAccountV1({ countryCode, body: bankBody }).pipe(
      switchMap((response) => {
        if (shouldFallbackToPersonal(countryCode, isBusiness) && response.status === 'FAILED') {
          return this.bankPersonalFallback(countryCode, bankBody, requestId, key, type, panelIndex);
        }
        return of(verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response }));
      }),
      catchError((error) => {
        if (shouldFallbackToPersonal(countryCode, isBusiness)) {
          return this.bankPersonalFallback(countryCode, bankBody, requestId, key, type, panelIndex);
        }
        return of(verificationFailed({ requestId, key, error }));
      })
    );
  }

  private bankPersonalFallback(
    countryCode: string,
    bankBody: BankVerificationRequestV1,
    requestId: string,
    key: string,
    type: CacheVerificationType,
    panelIndex: number
  ) {
    return this.bankApi.verifyBankAccountV1({ countryCode, body: { ...bankBody, isBusiness: false } }).pipe(
      map((response) => verificationResponseReceived({ requestId, key, verificationType: type, panelIndex, response })),
      catchError((error) => of(verificationFailed({ requestId, key, error })))
    );
  }

  private executePoll(requestId: string, key: string, resultId: string) {
    return of(null).pipe(
      delay(VERIFICATION_POLL_INTERVAL_MS),
      withLatestFrom(this.store.select(selectVerificationCacheEntry(key))),
      take(1),
      mergeMap(([, entry]) => {
        if (entry?.status !== 'pending') return of();
        if (entry.pollCount >= VERIFICATION_MAX_POLLS) return of(pollTimeout({ requestId, key }));
        return this.resultsApi.getVerificationResultV1({ requestId, resultId }).pipe(
          mergeMap(onPollResult(requestId, key, resultId)),
          catchError((error) => of(pollFailed({ requestId, key, error })))
        );
      })
    );
  }
}
